/**
 * Trigger a discovery run — `POST /sourcing/runs`, WP-W12. `web/shapes/sourcing.md`
 * §4 is the agreed shape. Rendered only for a session holding `recruiting.manage_jobs`
 * (`SourcingScreen` decides that — never the authorisation, which the server still
 * runs on every call).
 *
 * ── Only four channels are a value this control can ever submit ─────────────────
 *
 * The channel `<select>` is built from `LOW_RISK_SOURCING_CHANNELS` and nothing
 * else — the five high-risk channels are never an `<option>` here, so there is no
 * client state that could accidentally submit one. That is stricter than the server:
 * `openapi.yaml` allows any of the nine when `WP_SOURCING_ALLOW_TOS_RISK` and a
 * `wp_channel_policy` row both agree, but this screen does not offer the choice at
 * all (`web/shapes/sourcing.md` §1.2).
 *
 * ── The five quarantined channels are shown, always, disabled and explained ─────
 *
 * Directly below the form, exactly as `OutreachCompliancePanel`/`ChannelPolicyPanel`
 * treat their own high-risk rows: never hidden, never only a tooltip, a `lock` badge
 * plus a `StatusNote` at `high` severity carrying the ToS/legal warning — honesty over
 * enforcement, the same rule `nav/menu.ts` states for the whole app.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type {
  SourcingChannel,
  SourcingRunTriggerRequest,
  SourcingRunTriggerResponse,
} from '../../api/sourcing';
import {
  HIGH_RISK_SOURCING_CHANNELS,
  LOW_RISK_SOURCING_CHANNELS,
  SOURCING_CHANNEL_LABEL,
} from '../../recruiting/sourcing';
import type { WorkforceApiError } from '../../api/errors';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { StatusNote } from '../../states/StatusNote';
import type { PickerOption } from './CreateApplicationForm';

export interface SourcingTriggerPanelProps {
  /** `null` when the requisitions list is out of reach — the field falls back to an id input. */
  readonly requisitionOptions: readonly PickerOption[] | null;
  readonly pending: boolean;
  readonly error: WorkforceApiError | null;
  readonly triggered: SourcingRunTriggerResponse | null;
  readonly onTrigger: (body: SourcingRunTriggerRequest) => void;
}

export function SourcingTriggerPanel({
  requisitionOptions,
  pending,
  error,
  triggered,
  onTrigger,
}: SourcingTriggerPanelProps): ReactNode {
  const { t } = useI18n();
  const [requisitionId, setRequisitionId] = useState('');
  const [channel, setChannel] = useState<SourcingChannel>(LOW_RISK_SOURCING_CHANNELS[0] ?? 'email');

  const ready = requisitionId.trim() !== '';

  return (
    <section className="wp-section" aria-labelledby="w12-trigger-heading" data-testid="w12-trigger">
      <h2 className="wp-section__title" id="w12-trigger-heading">
        {t('sourcing.trigger.title')}
      </h2>
      <p className="wp-small wp-muted">{t('sourcing.trigger.intro')}</p>

      <form
        className="wp-filters"
        onSubmit={(event) => {
          event.preventDefault();
          if (!ready || pending) return;
          onTrigger({ requisition_id: requisitionId.trim(), channel });
        }}
      >
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w12-trigger-requisition">
            {t('sourcing.trigger.requisition')}
          </label>
          {requisitionOptions === null ? (
            <>
              <input
                className="wp-field__input"
                id="w12-trigger-requisition"
                data-testid="w12-trigger-requisition"
                type="text"
                value={requisitionId}
                autoComplete="off"
                onChange={(event) => {
                  setRequisitionId(event.target.value);
                }}
              />
              <p className="wp-small wp-muted">{t('sourcing.trigger.requisitionIdHint')}</p>
            </>
          ) : (
            <select
              className="wp-field__input"
              id="w12-trigger-requisition"
              data-testid="w12-trigger-requisition"
              value={requisitionId}
              onChange={(event) => {
                setRequisitionId(event.target.value);
              }}
            >
              <option value="">{t('sourcing.trigger.requisitionPlaceholder')}</option>
              {requisitionOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w12-trigger-channel">
            {t('sourcing.trigger.channel')}
          </label>
          <select
            className="wp-field__input"
            id="w12-trigger-channel"
            data-testid="w12-trigger-channel"
            value={channel}
            onChange={(event) => {
              setChannel(event.target.value as SourcingChannel);
            }}
          >
            {LOW_RISK_SOURCING_CHANNELS.map((value) => (
              <option key={value} value={value}>
                {t(SOURCING_CHANNEL_LABEL[value])}
              </option>
            ))}
          </select>
        </div>

        <div className="wp-filters__actions">
          <button
            className="wp-button wp-button--primary"
            type="submit"
            data-testid="w12-trigger-submit"
            disabled={!ready || pending}
          >
            {pending ? t('sourcing.trigger.submitting') : t('sourcing.trigger.submit')}
          </button>
        </div>
      </form>

      {error === null ? null : (
        <ApiErrorNote
          error={error}
          subject={t('sourcing.trigger.subjectWord')}
          testId="w12-trigger-error"
        />
      )}
      {triggered === null ? null : (
        <StatusNote
          severity="low"
          title={t('sourcing.trigger.ok', {
            leadsFound: triggered.leads_found,
            leadsNew: triggered.leads_new,
            candidatesCreated: triggered.candidates_created,
          })}
          testId="w12-trigger-ok"
        />
      )}

      <div className="wp-section" data-testid="w12-quarantine">
        <h3 className="wp-section__title">{t('sourcing.quarantine.title')}</h3>
        <p className="wp-small wp-muted">{t('sourcing.quarantine.intro')}</p>
        <ul className="wp-rows">
          {HIGH_RISK_SOURCING_CHANNELS.map((value) => (
            <li className="wp-row" key={value} data-testid={`w12-quarantine-${value}`}>
              <div className="wp-row__main">
                <p>
                  <Badge
                    severity="medium"
                    icon="lock"
                    label={t('sourcing.quarantine.badge')}
                    testId={`w12-quarantine-${value}-badge`}
                  />{' '}
                  <span data-testid={`w12-quarantine-${value}-channel`}>
                    {t(SOURCING_CHANNEL_LABEL[value])}
                  </span>
                </p>
                <StatusNote
                  severity="high"
                  title={t('sourcing.quarantine.highRisk.title')}
                  testId={`w12-quarantine-${value}-warning`}
                >
                  <p>{t('sourcing.quarantine.highRisk.body')}</p>
                </StatusNote>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
